import React, { useCallback, useEffect, useState } from 'react'
import { Form, Button, Container, Row, Col, Spinner } from 'react-bootstrap'
import { Footer, KonektoNavbar } from '../../components'
import './CreateRequest.scss'
import '../../../src/App.scss'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { requestService } from '../../services/requestService'
import { toast } from 'react-toastify'
import { COMPANY_TYPES } from '../../constants/config'
import { FaEnvelopeCircleCheck } from 'react-icons/fa6'

export default function CreateRequest() {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [searchParams] = useSearchParams()
  const query = searchParams.get('q')

  const { token } = useSelector(state => state.authentication)

  const [formData, setFormData] = useState({
    title: query,
    location: '',
    quantity: '',
    details: '',
    supplierTypes: [],
  })

  const [requestFormErrors, setRequestFormError] = useState(null)
  const [sendRequestError, setSendRequestError] = useState(null)
  const [updateLoader, setUpdateLoader] = useState(false)

  const [successMessageVisible, setSuccessMessageVisible] = useState(false)

  const handleSupplierTypeSelection = type => {
    let types = [...formData.supplierTypes]
    if (types.includes(type)) {
      // remove selection
      types = types.filter(t => t !== type)
    } else {
      //select it
      types.push(type)
    }

    setFormData({ ...formData, supplierTypes: types })
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleSubmit = async e => {
    setRequestFormError(null)
    setSendRequestError(null)
    e.preventDefault()

    let newErrors = {}

    if (formData?.title?.trim() === '') {
      newErrors = { ...newErrors, title: 'Ovo polje je obavezno' }
    }

    if (formData?.location?.trim() === '') {
      newErrors = { ...newErrors, location: 'Ovo polje je obavezno' }
    }

    if (formData?.details?.trim() === '') {
      newErrors = { ...newErrors, details: 'Ovo polje je obavezno' }
    }

    if (formData.supplierTypes.length === 0) {
      newErrors = { ...newErrors, supplierTypes: 'Ovo polje je obavezno' }
    }

    if (Object.keys(newErrors).length > 0) {
      setRequestFormError(newErrors)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setUpdateLoader(true)

    const supplierTypesString = formData.supplierTypes.join(',')

    const data = {
      title: formData.title,
      location: formData.location,
      quantity: formData.quantity,
      details: formData.details,
      supplier_type: supplierTypesString,
    }

    const response = await requestService.sendRequest(data)
    if (response.isOk) {
      setSuccessMessageVisible(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      setSendRequestError(response.message)
    }

    setUpdateLoader(false)
  }

  const resetForm = useCallback(() => {
    setSuccessMessageVisible(false)
    setFormData({
      title: '',
      location: '',
      quantity: '',
      details: '',
      supplierTypes: [],
    })
  }, [])

  const renderRequestSentMessage = useCallback(() => {
    return (
      <div className="form-group-wrapper request-sent-div">
        <FaEnvelopeCircleCheck className="envelope text-success" />
        <h3 className="text-success">{`Uspješno ste poslali zahtjev!`}</h3>
        <p className="text-center">
          Nakon što nađemo firme koje mogu ispuniti vaš zahtjev, spojiti ćemo
          vas putem poruka i poslati obavijest na mail.
          <br /> <b>Hvala što koristite Konekto!</b>
        </p>
        <Button
          onClick={resetForm}
          className="mt-3 rounded-pill primary-button">
          Pošalji novi zahtjev
        </Button>
      </div>
    )
  }, [])

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }, 100)
  }, [])

  return (
    <div id="create-request">
      <KonektoNavbar />
      <Container className="my-5 w-100 w-sm-50">
        {successMessageVisible ? (
          renderRequestSentMessage()
        ) : (
          <>
            {token ? (
              <Form onSubmit={handleSubmit}>
                <div className="form-group-wrapper shadow">
                  <h5 className="fw-bold fs-4">Zahtjev</h5>

                  <hr className="hr divider" />

                  <Form.Group controlId="title" className="mb-3">
                    <Form.Label>Naziv proizvoda ili servisa *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      placeholder="Unesite proizvod ili servis"
                      value={formData.title}
                      onChange={handleChange}
                    />
                    {requestFormErrors?.title ? (
                      <span className="field-error">
                        {requestFormErrors?.title}
                      </span>
                    ) : null}
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="location" className="mb-3">
                        <Form.Label>Lokacija *</Form.Label>
                        <Form.Control
                          type="text"
                          name="location"
                          placeholder="Vaša adresa"
                          value={formData.location}
                          onChange={handleChange}
                        />
                        {requestFormErrors?.location ? (
                          <span className="field-error">
                            {requestFormErrors?.location}
                          </span>
                        ) : null}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="quantity" className="mb-3">
                        <Form.Label>Količina</Form.Label>
                        <Form.Control
                          type="text"
                          name="quantity"
                          placeholder="Npr. 100 kg, 30 komada, 20 litara..."
                          value={formData.quantity}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <Form.Label>
                      Odaberite vrste firmi koje tražite *
                    </Form.Label>
                    <div className="d-flex flex-column flex-md-row">
                      {COMPANY_TYPES.map(type => {
                        const isTypeSelected =
                          formData.supplierTypes.includes(type)

                        return (
                          <div
                            key={type}
                            onClick={() => handleSupplierTypeSelection(type)}
                            className={`mt-2 mt-md-0 company-type ${isTypeSelected ? 'selected-type' : ''}`}>
                            {type}
                          </div>
                        )
                      })}
                    </div>
                    {requestFormErrors?.supplierTypes ? (
                      <span className="field-error">
                        {requestFormErrors?.supplierTypes}
                      </span>
                    ) : null}
                  </div>

                  <Form.Group controlId="details" className="mb-3">
                    <Form.Label className="w-50">Detalji zahtjeva *</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="details"
                      placeholder="Detalji zahtjeva"
                      value={formData.details}
                      onChange={handleChange}
                      maxLength={3000}
                      style={{ height: '200px' }} // Set the specific height for the textarea
                    />
                    {requestFormErrors?.details ? (
                      <span className="field-error">
                        {requestFormErrors?.details}
                      </span>
                    ) : null}
                  </Form.Group>
                </div>

                {sendRequestError ? (
                  <p className="field-error text-center mb-0 mt-2">
                    {sendRequestError}
                  </p>
                ) : null}

                <div className="text-end">
                  <Button
                    disabled={updateLoader}
                    variant="primary"
                    type="submit"
                    className="mt-3 rounded-pill primary-button">
                    {updateLoader ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      'Pošalji zahtjev'
                    )}
                  </Button>
                </div>
              </Form>
            ) : (
              <div className="text-center">
                <p className="fs-5 mt-5 text-danger">
                  Prijavite se kako biste poslali zahtjev
                </p>
                <button
                  onClick={() => navigate('/prijava')}
                  className="btn btn-primary primary-button rounded-pill">
                  Prijavi se
                </button>
              </div>
            )}
          </>
        )}
      </Container>
      <Footer />
    </div>
  )
}
